import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
} from "@material-ui/core";
import * as React from "react";
import {
  SlackChannelBotMembershipDTO,
  useGroopitApiContext,
  SlackChannelDTO,
} from "groopit-js";
import { Autocomplete } from "@material-ui/lab";

interface SlackChannelSelectorProps {
  connectionId: string;
  excludedChannels: string[];
  onChannelSelected: (channel: SlackSelectedChannelInfo | null) => void;
}

export interface SlackSelectedChannelInfo {
  channel: SlackChannelDTO;
  isBotInChannel: boolean;
  consentedToAddBot?: boolean;
}

export const SlackChannelSelector = (
  props: SlackChannelSelectorProps,
): JSX.Element => {
  const api = useGroopitApiContext();
  const [refocusChannelsPopup, setRefocusChannelsPopup] = React.useState(false);
  const [loadingChannels, setLoadingChannels] = React.useState(false);
  const [channels, setChannels] = React.useState<SlackChannelDTO[]>();
  const [channelsPopupOpen, setChannelsPopupOpen] = React.useState(false);
  const [nextCursor, setNextCursor] = React.useState<string>();
  const [channelSearch, setChannelSearch] = React.useState("");
  const [selectedChannel, setSelectedChannel] =
    React.useState<SlackChannelDTO | null>(null);
  const [botMembership, setBotMembership] =
    React.useState<SlackChannelBotMembershipDTO>();
  const [consented, setConsented] = React.useState(false);

  const getMoreChannels = () => {
    setLoadingChannels(true);
    api.slack
      .getUserChannels(props.connectionId, nextCursor, channelSearch)
      .then((results) => {
        setChannels([
          ...(channels ?? []),
          ...results.channels.filter(
            (c) => props.excludedChannels.indexOf(c.id) === -1,
          ),
        ]);
        setNextCursor(results.nextCursor);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoadingChannels(false);
      });
  };

  React.useEffect(() => {
    if (
      selectedChannel &&
      props.excludedChannels.includes(selectedChannel.id)
    ) {
      setSelectedChannel(null);
    }
    setChannels([
      ...(channels ?? []).filter(
        (c) => props.excludedChannels.indexOf(c.id) === -1,
      ),
    ]);
  }, [props.excludedChannels]);

  React.useEffect(getMoreChannels, []);

  React.useEffect(() => {
    if (selectedChannel) {
      api.slack
        .getBotMembership(selectedChannel.id, props.connectionId)
        .then(setBotMembership)
        .catch((error) => {
          console.log(error);
        });
    } else {
      props.onChannelSelected(null);
    }
  }, [selectedChannel]);

  React.useEffect(() => {
    if (botMembership && selectedChannel) {
      props.onChannelSelected({
        channel: selectedChannel,
        isBotInChannel: botMembership.isBotInChannel,
        consentedToAddBot: consented,
      });
    }
  }, [botMembership, consented]);

  return (
    <>
      {channels ? (
        <>
          <div className="flex gap-2">
            <Autocomplete
              className="flex-grow"
              renderInput={(params) => (
                <TextField {...params} label="Select Slack Channel:" />
              )}
              renderOption={(channel) => (
                <>
                  {channel.isPrivate ? (
                    <span className="glyphicon glyphicon-lock w-4 mr-1"></span>
                  ) : (
                    <span className="w-4 mr-1 text-center">#</span>
                  )}
                  {channel.name}
                </>
              )}
              getOptionLabel={(channel) => {
                if (channel.isPrivate) {
                  return `\u{1F512}${channel.name}`;
                } else {
                  return `#${channel.name}`;
                }
              }}
              clearOnBlur={false}
              open={channelsPopupOpen}
              onOpen={() => setChannelsPopupOpen(true)}
              onClose={() => setChannelsPopupOpen(false)}
              inputValue={channelSearch}
              onInputChange={(event, value) => {
                setChannelSearch(value);
              }}
              value={selectedChannel}
              onChange={(event, value) => {
                if (value) {
                  if (botMembership?.id !== value.id) {
                    setBotMembership(undefined);
                  }
                }
                setSelectedChannel(value);
              }}
              noOptionsText="No Results"
              options={channels}
            />
            {loadingChannels ? (
              <CircularProgress />
            ) : (
              nextCursor && (
                <button
                  className="btn btn-subtle btn-settings flex-shrink-0"
                  onMouseDown={() => {
                    if (channelsPopupOpen) {
                      setRefocusChannelsPopup(true);
                    }
                  }}
                  onClick={() => {
                    getMoreChannels();
                    if (refocusChannelsPopup) {
                      setRefocusChannelsPopup(false);
                      setChannelsPopupOpen(true);
                    }
                  }}
                >
                  Load More
                </button>
              )
            )}
          </div>
          {selectedChannel && (
            <>
              {botMembership ? (
                <>
                  {!botMembership.isBotInChannel && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={consented}
                          onChange={(event) =>
                            setConsented(event.target.checked)
                          }
                        />
                      }
                      label="To enable data sharing, add Groopit to this channel."
                    />
                  )}
                </>
              ) : (
                <div className="text-center mt-8">
                  <CircularProgress />
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <div className="text-center mt-8">
          <CircularProgress />
        </div>
      )}
    </>
  );
};
